import type {
  AnyEquation,
  Equation,
  EquationEvaluation,
  EquationEvaluationState,
  EvaluationResult,
} from "@src/features/equation-editor/equation";
import type { GetAppState, SetAppState } from "../app-store-contract";
import type { AppState } from "../app-state";
import {
  addEigenbasis,
  addInverseTransform,
  addMatrix,
  addMatrixPreset,
  addRepresentativeEigenvector,
  addVector,
  deleteEquationItem,
  moveEquationItem,
  moveEquationItemTo,
  setMatrixDuration,
  setMatrixEntry,
  setVectorCoordinate,
} from "@src/features/equation-editor/editor-operations";
import {
  expressionDraft,
  matrixEntryIndex,
  nodeId,
  vectorCoordinateIndex,
} from "@src/features/equation-editor/equation-values";
import type { EquationEditorActions } from "@src/features/equation-editor/editor-actions";
import type { EquationEvaluationCoordinator } from "@src/features/equation-editor/evaluation/evaluation-coordinator";
import { evaluationStateFromResult } from "@src/features/equation-editor/equation-operations";
import { editEquation } from "@src/features/equation-editor/equation-operations";
import { matrixDurationMs } from "@src/features/equation-editor/equation-policy";
import { resetPlayback } from "@src/features/playback/playback";
import type { Dimension } from "@src/math/types";

export function createEquationActions(
  set: SetAppState,
  get: GetAppState,
  evaluationCoordinator: EquationEvaluationCoordinator,
): EquationEditorActions {
  const createId = (): string => crypto.randomUUID();
  const mutateEquation = (
    change: <D extends Dimension>(equation: Equation<D>) => boolean,
    coalesceEvaluation = false,
  ): boolean => {
    const state = get();
    return state.activeDimension === 2
      ? mutateKnownEquation(
          state.equations[2],
          change,
          set,
          (equation) => {
            evaluationCoordinator.schedule(
              equation,
              coalesceEvaluation,
              (result) => {
                applyKnownEvaluationResult(
                  set,
                  result,
                  (current) => current.equations[2],
                  (current, evaluated) => ({
                    equations: { ...current.equations, 2: evaluated },
                  }),
                );
              },
            );
          },
          (equation) => replaceEquation(state, equation),
        )
      : mutateKnownEquation(
          state.equations[3],
          change,
          set,
          (equation) => {
            evaluationCoordinator.schedule(
              equation,
              coalesceEvaluation,
              (result) => {
                applyKnownEvaluationResult(
                  set,
                  result,
                  (current) => current.equations[3],
                  (current, evaluated) => ({
                    equations: { ...current.equations, 3: evaluated },
                  }),
                );
              },
            );
          },
          (equation) => replaceEquation(state, equation),
        );
  };
  const updateMatrixDuration = (id: string, durationMs: number): boolean => {
    const state = get();
    const validatedId = nodeId(id);
    const parsedDuration = matrixDurationMs(durationMs);
    return state.activeDimension === 2
      ? updateKnownMatrixDuration(
          state.equations[2],
          validatedId,
          parsedDuration,
          set,
          (equation) => replaceEquation(state, equation),
        )
      : updateKnownMatrixDuration(
          state.equations[3],
          validatedId,
          parsedDuration,
          set,
          (equation) => replaceEquation(state, equation),
        );
  };
  const commandEquation = (
    command: <D extends Dimension>(equation: Equation<D>) => void,
  ): void => {
    mutateEquation((equation) => {
      command(equation);
      return true;
    });
  };

  return {
    setMatrixEntry: (id, index, value) => {
      let parsedValue;
      try {
        parsedValue = expressionDraft(value);
      } catch {
        return false;
      }
      return mutateEquation(
        (equation) =>
          setMatrixEntry(
            equation,
            nodeId(id),
            matrixEntryIndex(equation.dimension, index),
            parsedValue,
          ),
        true,
      );
    },
    setMatrixDuration: updateMatrixDuration,
    setVectorCoordinate: (id, index, value) => {
      let parsedValue;
      try {
        parsedValue = expressionDraft(value);
      } catch {
        return false;
      }
      return mutateEquation(
        (equation) =>
          setVectorCoordinate(
            equation,
            nodeId(id),
            vectorCoordinateIndex(equation.dimension, index),
            parsedValue,
          ),
        true,
      );
    },
    addMatrix: () => {
      commandEquation((equation) => {
        addMatrix(equation, createId);
      });
    },
    addVector: () => {
      commandEquation((equation) => {
        addVector(equation, createId);
      });
    },
    addMatrixPreset: (presetId) => {
      commandEquation((equation) => {
        addMatrixPreset(equation, presetId, createId);
      });
    },
    addInverseTransform: () => {
      commandEquation((equation) => {
        addInverseTransform(equation, createId);
      });
    },
    addEigenbasis: () => {
      commandEquation((equation) => {
        addEigenbasis(equation, createId);
      });
    },
    addEigenvector: () => {
      commandEquation((equation) => {
        addRepresentativeEigenvector(equation, createId);
      });
    },
    deleteItem: (kind, id) => {
      commandEquation((equation) => {
        deleteEquationItem(equation, kind, nodeId(id));
      });
    },
    moveItem: (kind, id, direction) => {
      let result: number | null = null;
      mutateEquation((equation) => {
        result = moveEquationItem(equation, kind, nodeId(id), direction);
        return result !== null;
      });
      return result;
    },
    moveItemTo: (kind, id, targetId, side) =>
      void mutateEquation(
        (equation) =>
          moveEquationItemTo(
            equation,
            kind,
            nodeId(id),
            nodeId(targetId),
            side,
          ) !== null,
      ),
  };
}

function mutateKnownEquation<D extends Dimension>(
  current: Equation<D>,
  change: (equation: Equation<D>) => boolean,
  set: SetAppState,
  schedule: (equation: Equation<D>) => void,
  replace: (equation: Equation<D>) => AppState,
): boolean {
  const { equation: draft, result: changed } = editEquation(current, change);
  if (!changed) return false;
  draft.evaluation = {
    kind: "evaluating",
    value: current.evaluation.value,
  };
  set(replace(draft));
  schedule(draft);
  return true;
}

function updateKnownMatrixDuration<D extends Dimension>(
  current: Equation<D>,
  id: string,
  durationMs: number,
  set: SetAppState,
  replace: (equation: Equation<D>) => AppState,
): boolean {
  const { equation } = editEquation(current, () => undefined);
  if (!setMatrixDuration(equation, id, durationMs)) return false;
  equation.evaluation = updateEvaluationDuration(
    current.evaluation,
    id,
    durationMs,
  );
  set(replace(equation));
  return true;
}

function updateEvaluationDuration<D extends Dimension>(
  evaluation: EquationEvaluationState<D>,
  id: string,
  durationMs: number,
): EquationEvaluationState<D> {
  if (evaluation.kind !== "stale" || evaluation.checkpoint === undefined)
    return {
      ...evaluation,
      value: updateEvaluatedMatrixDuration(evaluation.value, id, durationMs),
    };
  return {
    ...evaluation,
    value: updateEvaluatedMatrixDuration(evaluation.value, id, durationMs),
    checkpoint: {
      ...evaluation.checkpoint,
      evaluation: updateEvaluatedMatrixDuration(
        evaluation.checkpoint.evaluation,
        id,
        durationMs,
      ),
    },
  };
}

function updateEvaluatedMatrixDuration<D extends Dimension>(
  evaluation: EquationEvaluation<D>,
  id: string,
  durationMs: number,
): EquationEvaluation<D> {
  return {
    ...evaluation,
    matrices: evaluation.matrices.map((matrix) =>
      matrix.id === id ? { ...matrix, durationMs } : matrix,
    ),
  };
}

function applyKnownEvaluationResult<D extends Dimension>(
  set: SetAppState,
  result: EvaluationResult<D>,
  select: (state: AppState) => Equation<D>,
  replace: (state: AppState, equation: Equation<D>) => Partial<AppState>,
): void {
  set((state) => {
    const current = select(state);
    const equation: Equation<D> = {
      ...current,
      evaluation: evaluationStateFromResult(
        rebaseEvaluationDurations(result, current),
        current.evaluation.value,
      ),
    };
    return replace(state, equation);
  });
}

function rebaseEvaluationDurations<D extends Dimension>(
  result: EvaluationResult<D>,
  equation: Equation<D>,
): EvaluationResult<D> {
  const durations = new Map(
    equation.matrices.map(({ id, durationMs }) => [id, durationMs]),
  );
  const rebase = (
    evaluation: EquationEvaluation<D>,
  ): EquationEvaluation<D> => ({
    ...evaluation,
    matrices: evaluation.matrices.map((matrix) => ({
      ...matrix,
      durationMs: durations.get(matrix.id) ?? matrix.durationMs,
    })),
  });
  if (result.kind === "current")
    return { ...result, value: rebase(result.value) };
  return {
    ...result,
    ...(result.checkpoint
      ? {
          checkpoint: {
            ...result.checkpoint,
            evaluation: rebase(result.checkpoint.evaluation),
          },
        }
      : {}),
  };
}

function replaceEquation(state: AppState, equation: AnyEquation) {
  const equations =
    equation.dimension === 2
      ? {
          ...state.equations,
          2: equation,
        }
      : {
          ...state.equations,
          3: equation,
        };
  return {
    ...state,
    equations,
    animation: resetPlayback(state.animation),
  };
}
